// Client guard against the 402 request loop (3b, Tag-8 live check 2026-10-04): when the server 402s a
// product API with a billing code but /api/auth/me still reports access (e.g. a lapsed grace period the
// server has not flipped to `expired` yet), invalidating /api/auth/me on every 402 refetched the page's
// queries, which 402'd again, until the auth rate limit answered 429. Now each 402 code may trigger
// at most ONE /api/auth/me re-check; a repeat of the same code routes straight to the PaymentStep.
// The server stays the authority: this only stops the client from spinning.

import { useSyncExternalStore } from "react";

export type Billing402Decision = "ignore" | "recheck" | "payment";

// Every 402 from the access gate is a billing denial (SUBSCRIPTION_EXPIRED, PAYMENT_REQUIRED, TRIAL_EXPIRED,
// NO_ACCESS; server/services/accessControl.ts). A 402 without a code counts as its own kind.
export function billing402Key(code: string | undefined): string {
  return code || "UNKNOWN";
}

/**
 * Pure decision for one failed request. `recheckedCodes` holds the 402 codes that already triggered their
 * one /api/auth/me re-check in this page load.
 */
export function decideBilling402(
  status: number | undefined,
  code: string | undefined,
  recheckedCodes: ReadonlySet<string>,
): Billing402Decision {
  if (status !== 402) return "ignore";
  return recheckedCodes.has(billing402Key(code)) ? "payment" : "recheck";
}

// Module state for the page load: a reload (Stripe/portal return, logout → login) starts fresh.
const recheckedCodes = new Set<string>();
let paymentForced = false;
const listeners = new Set<() => void>();

/** Feed a thrown request error; returns the decision so the caller can act on it. */
export function handleBilling402(error: unknown): Billing402Decision {
  const e = error as { status?: number; data?: { code?: string } } | undefined;
  const decision = decideBilling402(e?.status, e?.data?.code, recheckedCodes);
  if (decision === "recheck") recheckedCodes.add(billing402Key(e?.data?.code));
  if (decision === "payment" && !paymentForced) {
    paymentForced = true;
    listeners.forEach((l) => l());
  }
  return decision;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

function getSnapshot(): boolean {
  return paymentForced;
}

/** True once a 402 code repeated after its re-check: the App shows the PaymentStep. */
export function usePaymentForced(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
